import { createTheme } from '@mui/material/styles';

const navy = '#1e3669';
const green = '#02a552';
const line = '#e5eaf1';

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: { main: navy, dark: '#132956', light: '#47618f', contrastText: '#ffffff' },
    secondary: { main: green, dark: '#028a47', light: '#dff7e8', contrastText: '#ffffff' },
    background: { default: '#f5f7fb', paper: '#ffffff' },
    text: { primary: '#18233d', secondary: '#64748b' },
    success: { main: green, light: '#e5f8ed', dark: '#087d3e' },
    warning: { main: '#d99114', light: '#fff5df', dark: '#9a5e00' },
    error: { main: '#d83a45', light: '#ffeaeb', dark: '#b3262f' },
    info: { main: '#3874c8', light: '#e9f1ff', dark: '#29569b' },
    divider: line,
  },
  typography: {
    fontFamily: "'Montserrat', 'Avenir Next', system-ui, -apple-system, 'Segoe UI', sans-serif",
    fontWeightRegular: 500,
    fontWeightMedium: 650,
    fontWeightBold: 850,
    h4: { fontWeight: 850, letterSpacing: '-0.025em' },
    h5: { fontWeight: 850, letterSpacing: '-0.02em' },
    h6: { fontWeight: 850, letterSpacing: '-0.015em' },
    overline: { fontSize: '0.66rem', fontWeight: 900, letterSpacing: '0.11em' },
  },
  shape: { borderRadius: 12 },
  components: {
    MuiCssBaseline: { styleOverrides: { body: { backgroundColor: '#f5f7fb', color: '#18233d' }, '*, *::before, *::after': { boxSizing: 'border-box' } } },
    MuiPaper: { styleOverrides: { root: { border: `1px solid ${line}`, borderRadius: 12, boxShadow: '0 5px 18px rgba(30,54,105,.055)' } } },
    MuiCard: { styleOverrides: { root: { border: `1px solid ${line}`, boxShadow: '0 5px 18px rgba(30,54,105,.055)' } } },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontSize: '0.78rem',
          fontWeight: 800,
          letterSpacing: '0.01em',
          minHeight: 38,
          padding: '7px 12px',
          textTransform: 'none',
          width: 'auto',
          transition: 'box-shadow .16s ease, transform .16s ease, background .16s ease',
          '@media (max-width:900px)': {
            minHeight: 44,
            paddingInline: 16,
          },
        },
        containedPrimary: {
          backgroundColor: green,
          boxShadow: '0 7px 16px rgba(2,165,82,.18)',
          '&:hover': {
            backgroundColor: '#028e47',
            boxShadow: '0 10px 22px rgba(2,165,82,.22)',
            transform: 'translateY(-1px)',
          },
        },
        containedSecondary: {
          backgroundColor: navy,
          boxShadow: '0 7px 16px rgba(30,54,105,.16)',
          '&:hover': {
            backgroundColor: '#274782',
            boxShadow: '0 10px 22px rgba(30,54,105,.22)',
            transform: 'translateY(-1px)',
          },
        },
        outlined: {
          borderColor: '#9caccc',
          color: navy,
          '&:hover': {
            backgroundColor: '#eaf8ef',
            borderColor: '#68bd8c',
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          color: '#566783',
          '@media (max-width:900px)': { minHeight: 44, minWidth: 44 },
          '&:hover': { backgroundColor: '#eaf8ef', color: navy },
        },
      },
    },
    MuiChip: { styleOverrides: { root: { borderRadius: 7, fontSize: '0.68rem', fontWeight: 800, letterSpacing: '0.01em' } } },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontSize: '0.82rem',
          '@media (max-width:900px)': { minHeight: 44 },
          '& .MuiOutlinedInput-notchedOutline': { borderColor: '#d9e1ec' },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#9caccc' },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: green, borderWidth: 1, boxShadow: '0 0 0 3px rgba(2,165,82,.12)' },
        },
      },
    },
    MuiInputLabel: { styleOverrides: { root: { fontSize: '0.8rem', fontWeight: 700, '&.Mui-focused': { color: green } } } },
    MuiTableHead: { styleOverrides: { root: { backgroundColor: '#f7f9fc' } } },
    MuiTableCell: { styleOverrides: { head: { color: '#72809a', fontSize: '0.65rem', fontWeight: 900, letterSpacing: '0.1em', paddingBlock: 12, textTransform: 'uppercase' }, root: { borderColor: '#edf1f6' }, body: { color: '#334155', fontSize: '0.8rem', paddingBlock: 13 } } },
    MuiTabs: { styleOverrides: { root: { borderBottom: `1px solid ${line}` }, indicator: { backgroundColor: green, height: 3 } } },
    MuiTab: { styleOverrides: { root: { color: '#64748b', fontSize: '0.76rem', fontWeight: 800, minHeight: 46, textTransform: 'none', '@media (max-width:900px)': { minHeight: 48 }, '&.Mui-selected': { color: navy } } } },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          borderColor: '#d9e1ec',
          color: '#64748b',
          fontSize: '0.72rem',
          fontWeight: 800,
          minHeight: 38,
          textTransform: 'none',
          '@media (max-width:900px)': { minHeight: 44 },
          '&.Mui-selected': { backgroundColor: navy, color: '#fff' },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          border: `1px solid ${line}`,
          borderRadius: 14,
          boxShadow: '0 24px 70px rgba(18,41,86,.22)',
          '@media (max-width:600px)': { borderRadius: 12, margin: 12, maxWidth: 'calc(100% - 24px)', width: 'calc(100% - 24px)' },
        },
      },
    },
    MuiDialogTitle: { styleOverrides: { root: { color: '#18233d', fontSize: '1.06rem', fontWeight: 850, padding: '20px 24px 12px', '@media (max-width:600px)': { padding: '18px 18px 10px' } } } },
    MuiDialogContent: { styleOverrides: { root: { padding: '16px 24px 20px', '@media (max-width:600px)': { padding: '14px 18px 18px' } } } },
    MuiDialogActions: { styleOverrides: { root: { borderTop: `1px solid ${line}`, gap: 10, padding: '14px 24px', '@media (max-width:600px)': { flexWrap: 'wrap', padding: '12px 18px' }, '& > :not(style) ~ :not(style)': { marginLeft: 0 } } } },
    MuiDrawer: { styleOverrides: { paper: { borderLeft: `1px solid ${line}`, boxShadow: '-12px 0 32px rgba(18,41,86,.12)' } } },
    MuiMenu: { styleOverrides: { paper: { border: `1px solid ${line}`, borderRadius: 10, boxShadow: '0 12px 28px rgba(18,41,86,.14)' } } },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontSize: '0.8rem',
          minHeight: 36,
          '& > svg': { fontSize: '1rem', marginRight: 9 },
          '&.Mui-selected': { backgroundColor: '#eaf8ef', color: navy, fontWeight: 800 },
        },
      },
    },
    MuiAutocomplete: { styleOverrides: { paper: { border: `1px solid ${line}`, borderRadius: 10, boxShadow: '0 12px 28px rgba(18,41,86,.14)' } } },
    MuiTooltip: { styleOverrides: { tooltip: { backgroundColor: '#18233d', borderRadius: 6, fontSize: '0.7rem' } } },
    MuiPaginationItem: { styleOverrides: { root: { borderRadius: 7, fontSize: '0.75rem', fontWeight: 800, '&.Mui-selected': { backgroundColor: navy, color: '#fff' } } } },
    MuiStepLabel: { styleOverrides: { label: { color: '#72809a', fontSize: '0.76rem', fontWeight: 750, '&.Mui-active, &.Mui-completed': { color: navy, fontWeight: 850 } } } },
    MuiStepIcon: { styleOverrides: { root: { '&.Mui-active, &.Mui-completed': { color: green } } } },
    MuiAlert: { styleOverrides: { root: { borderRadius: 10, fontSize: '0.8rem' } } },
    MuiLinearProgress: { styleOverrides: { root: { borderRadius: 999, height: 7 }, bar: { borderRadius: 999 } } },
    MuiTextField: { defaultProps: { size: 'small' } },
  },
});

export default theme;
